import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { User } from '@auth0/auth0-react';
import { getProfile, updateProfile as updateProfileAPI, ProfileData } from './services/profileService';
import { buildAuth0AuthorizationParams } from '@/lib/auth0AuthorizationParams';

// Re-export ProfileData for use in other components
export type { ProfileData };

// Empty profile - no hardcoded defaults
const emptyProfile: ProfileData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  avatar: null,
};

/** Fill blank profile names/email from Auth0 so header + dashboards match DynamoDB-backed page when DB rows are incomplete. */
const mergeDbProfileWithAuth0 = (db: ProfileData, authUser: User | undefined): ProfileData => {
  if (!authUser) return db;

  const fromAuth = extractProfileFromAuth0(authUser);

  const firstName = (db.firstName || '').trim() || (fromAuth.firstName || '').trim();
  const lastName = (db.lastName || '').trim() || (fromAuth.lastName || '').trim();
  const email = ((db.email || '').trim() || fromAuth.email || '').trim();

  return {
    ...db,
    firstName,
    lastName,
    email,
  };
};

interface ProfileContextType {
  profile: ProfileData;
  setProfile: React.Dispatch<React.SetStateAction<ProfileData>>;
  isLoading: boolean;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Helper function to extract profile from Auth0 user
const extractProfileFromAuth0 = (user: any): ProfileData => {
  if (!user) return emptyProfile;
  
  // Extract name parts from Auth0 user
  const fullName = user.name || '';
  const email = user.email || '';
  
  // Don't use email as name - if name is email, treat it as empty
  const isNameAnEmail = fullName.includes('@');
  const nameParts = isNameAnEmail ? [] : fullName.split(' ');
  
  const firstName = user.given_name || (nameParts.length > 0 ? nameParts[0] : '');
  const lastName = user.family_name || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');
  const avatar = user.picture || null;
  
  return {
    firstName,
    lastName,
    email,
    phone: '', // No default - user must enter
    role: '', // No default - user must enter
    avatar,
  };
};

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [profile, setProfile] = useState<ProfileData>(emptyProfile);
  const [isLoading, setIsLoading] = useState(true);
  
  // Helper function to get token with audience
  const getToken = async (): Promise<string> => {
    return await getAccessTokenSilently({
      authorizationParams: buildAuth0AuthorizationParams(),
    });
  };
  
  // Load profile from API on mount and when auth state changes
  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Always load from API - single source of truth
        const apiProfile = await getProfile(getToken);

        if (apiProfile) {
          // DynamoDB rows may omit first/last; merge Auth0 name claims for header consistency.
          setProfile(mergeDbProfileWithAuth0(apiProfile, user));
        } else {
          // Profile doesn't exist in API yet, initialize from Auth0 and save to API
          const auth0Profile = extractProfileFromAuth0(user);
          if (auth0Profile.email) {
            setProfile(auth0Profile);
            // Save to API immediately so it persists
            try {
              await updateProfileAPI(auth0Profile, getToken);
            } catch (error) {
              console.warn('Failed to save initial profile to API:', error);
              // Still set the profile locally even if API save fails
            }
          } else {
            setProfile(emptyProfile);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        // On error, fall back to Auth0 data (but don't persist)
        if (user) {
          const auth0Profile = extractProfileFromAuth0(user);
          setProfile(mergeDbProfileWithAuth0(auth0Profile, user));
        } else {
          setProfile(emptyProfile);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProfile();
  }, [isAuthenticated, user]);
  
  // Wrapper for setProfile that saves to API (DynamoDB)
  const updateProfile = async (newProfile: ProfileData | ((prev: ProfileData) => ProfileData)) => {
    if (!isAuthenticated) {
      console.warn('Cannot update profile: not authenticated');
      return;
    }
    
    const updated = typeof newProfile === 'function' ? newProfile(profile) : newProfile;
    
    // Save to API first (DynamoDB is the source of truth)
    try {
      const savedProfile = await updateProfileAPI(updated, getToken);
      setProfile(mergeDbProfileWithAuth0(savedProfile, user));
    } catch (error) {
      console.error('Error saving profile to API:', error);
      // Don't update local state if API save fails - keep existing data
      throw error; // Re-throw so caller can handle the error
    }
  };
  
  return (
    <ProfileContext.Provider value={{ profile, setProfile: updateProfile, isLoading }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}; 